// Utils
import { post as postV2 } from '@shopee-user/fetch-utils-v2';
import { formatPhoneNumberForServer } from '@shopee-user/authentication-utils';
import { getUserLanguage } from '@shopee-user/platform';
import { isFeatureEnabled } from '@shopee-user/business-customization-logic';
import { Feature } from '@shopee-user/feature-toggle';

// Constants
import { OtpType, VcodeOperation } from '@shopee-user/otp-constants';

// Types
import type {
  GetOTPSettingsResponse,
  SendOTPResponse,
  VerifyOtpApiResponse,
  VerifyOtpResponse,
} from './types';

const isLanguageSentInSendOtpRequest = isFeatureEnabled(
  Feature.SendLanguageInSendOtp
);

/**
 * Get OTP settings
 *
 * > userid can be provided implicitly by login status
 * Reference: https://confluence.shopee.io/x/CqqYFg
 */
export async function getOTPSettings({
  phone,
  email,
  avToken,
  mToken,
  encryptedEmail,
  operation,
  supportedChannels,
  captchaSignature,
  supportSession,
}: {
  phone?: string;
  email?: string;
  encryptedEmail?: string;
  /**
   * @deprecated. avToken means encrypted userid.
   * It is only used for password login on new device trigger OTP verfication, and BE will remove this feature after 2FA IVS integration.
   * Please consider to use encryptedAuthKey if encrypted phone/email is needed.
   */
  avToken?: string;
  mToken?: string;
  operation: VcodeOperation;
  supportedChannels?: OtpType[];
  captchaSignature?: string;
  supportSession?: boolean;
}): Promise<GetOTPSettingsResponse> {
  const formattedPhoneNumber = phone
    ? await formatPhoneNumberForServer(phone)
    : undefined;
  return postV2(
    '/api/v4/account/business/get_otp_settings',
    {
      phone: formattedPhoneNumber,
      email,
      encrypted_email: encryptedEmail,
      av_token: avToken, // encrypted userid - used for password login in new device and turn to OTP login
      m_token: mToken, // encrypted phone number
      operation,
      supported_channels: supportedChannels,
      captcha_signature: captchaSignature,
      support_session: supportSession,
    },
    {
      antiFraudConfig: {
        deviceFingerPrint: {
          dataFormatter: dfp => ({
            security_device_fingerprint: dfp,
          }),
        },
      },
    }
  );
}

export async function sendOTP({
  phone,
  email,
  encryptedEmail,
  avToken,
  mToken,
  operation,
  channel = OtpType.Sms,
  captchaSignature,
  supportedChannels,
  supportSession,
}: {
  phone?: string;
  email?: string;
  encryptedEmail?: string;
  avToken?: string;
  mToken?: string;
  operation: VcodeOperation;
  channel?: OtpType;
  captchaSignature?: string;
  supportedChannels?: OtpType[];
  supportSession?: boolean;
}) {
  const formattedPhoneNumber = phone
    ? await formatPhoneNumberForServer(phone)
    : undefined;
  const reqParams = {
    operation,
    channel,
    phone: formattedPhoneNumber,
    email: email || undefined,
    encrypted_email: encryptedEmail,
    m_token: mToken || undefined,
    av_token: avToken || undefined,
    captcha_signature: captchaSignature,
    lang: isLanguageSentInSendOtpRequest ? getUserLanguage() : undefined,
    supported_channels: supportedChannels,
    support_session: supportSession,
  };
  return postV2<SendOTPResponse>(
    '/api/v4/account/business/send_otp',
    reqParams,
    {
      antiFraudConfig: {
        deviceFingerPrint: {
          dataFormatter: dfp => ({
            security_device_fingerprint: dfp,
          }),
        },
      },
    }
  );
}

export async function verifyOTP({
  otp,
  phone,
  email,
  encryptedEmail,
  mToken,
  avToken,
  operation,
  supportSession,
}: {
  phone?: string;
  email?: string;
  encryptedEmail?: string;
  mToken?: string;
  avToken?: string;
  operation: VcodeOperation;
  otp: string;
  supportSession?: boolean;
}): Promise<VerifyOtpResponse> {
  const requestBody = {
    otp,
    phone,
    email,
    encrypted_email: encryptedEmail,
    m_token: mToken || undefined,
    av_token: avToken || undefined,
    operation,
    support_session: supportSession,
  };
  const api = '/api/v4/account/business/verify_otp';
  const response = await postV2<VerifyOtpApiResponse>(api, requestBody, {
    antiFraudConfig: {
      deviceFingerPrint: {
        dataFormatter: dfp => ({
          security_device_fingerprint: dfp,
        }),
      },
    },
  });
  return {
    request: {
      api,
    },
    response,
  };
}
